import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";
import { listActiveCompanies } from "../actions/companyActions";
import { exportVerificationBankSheetTXT, exportVerificationBankSheetDOC, exportVerificationBankSheetXLSX } from "../actions/exportActions";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
} from "@mui/material";
import { Search as SearchIcon, Download as DownloadIcon } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const BANK_PAIRS = [
  { caseField: "HDFC_HLLoans",   title: "HDFC HL Loans"   },
  { caseField: "HDFCCards",      title: "HDFC Cards"       },
  { caseField: "HDFCLoans",      title: "HDFC Loans"       },
  { caseField: "Chola",          title: "Chola"            },
  { caseField: "IDFC",           title: "IDFC"             },
  { caseField: "INCRED",         title: "INCRED"           },
  { caseField: "AakashaFinance", title: "Aakasha Finance"  },
  { caseField: "TATA_CAPITAL",   title: "TATA Capital"     },
  { caseField: "BIZ_LOAN",       title: "Biz Loan"         },
  { caseField: "SK_Finance",     title: "S.K Finance"      },
  { caseField: "SURYODAY",       title: "SURYODAY"         },
  { caseField: "PIRAMAL",        title: "PIRAMAL"          },
  { caseField: "TOYOTA",         title: "TOYOTA"           },
  { caseField: "BMW_Financial",  title: "BMW Financial"    },
  { caseField: "ESAF",           title: "ESAF"             },
  { caseField: "Kotak",          title: "Kotak"            },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const COLUMNS = [
  { title: "S No",                  field: "sNo" },
  { title: "Emp ID",                field: "employeeId" },
  { title: "FE Name",               field: "feName" },
  { title: "PAN",                   field: "pan" },
  { title: "Bank Name",             field: "bankName" },
  { title: "Bank Type",             field: "bankType" },
  { title: "Account No.",           field: "accountNo" },
  { title: "IFSC Code",             field: "ifscCode" },
  { title: "Remarks for Account",   field: "remarksForAccount", redText: true },
  { title: "Main Portfolio",        field: "mainPortfolio" },
  { title: "Zone",                  field: "zone" },
  { title: "State",                 field: "state" },
  { title: "Department",            field: "department" },
  { title: "Retainership",          field: "retainership",        currency: true, color: "#7c3aed" },
  { title: "No of Days on Field",   field: "daysOnField" },
  { title: "Reimbursement / Day",   field: "reimbursementPerDay", currency: true, color: "#1d4ed8" },
  { title: "Total Reimbursement",   field: "totalReimbursement",  currency: true, color: "#15803d" },
  { title: "Total Cases",           field: "totalCases" },
  { title: "Conveyance",            field: "conveyance",          currency: true, editable: true },
  { title: "Penalty",               field: "penalty",             currency: true, editable: true },
  { title: "Total Amount",          field: "totalAmount",         currency: true, color: "#4338ca", getValue: (row) => (row.totalAmount ?? 0) + (row.totalReimbursement ?? 0) + (row.conveyance ?? 0) - (row.penalty ?? 0) },
  { title: "TDS%",                  field: "tdsPercent" },
  { title: "TDS Amount",            field: "tdsAmount",           currency: true, color: "#b91c1c", getValue: (row) => { const total = (row.totalAmount ?? 0) + (row.totalReimbursement ?? 0) + (row.conveyance ?? 0) - (row.penalty ?? 0); return Math.round(total * (row.tdsPercent ?? 0) / 100 * 100) / 100; } },
  { title: "Advance",               field: "advance",             currency: true, editable: true },
  { title: "Payable Amount",        field: "payableAmount",       currency: true, color: "#0f766e" },
];

const renderCell = (row, col) => {
  const val = col.getValue ? col.getValue(row) : row[col.field];
  if (col.redText) {
    return <span style={{ color: "#dc2626" }}>{val || "—"}</span>;
  }
  if (col.currency) {
    return (
      <span style={{ fontWeight: 500, color: col.color || "inherit" }}>
        {val != null ? `₹${Number(val).toLocaleString()}` : "—"}
      </span>
    );
  }
  return val ?? "—";
};

const VerificationReport = () => {
  const dispatch = useDispatch();
  const role = useSelector((state) => state.getRoleInfo?.getRoleData || {});
  const [month, setMonth]           = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear]             = useState(currentYear);
  const [company, setCompany]       = useState("");
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [generated, setGenerated]   = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [exportDialog, setExportDialog] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [inlineEdit, setInlineEdit]     = useState({ empId: null, field: null });
  const [inlineValue, setInlineValue]   = useState('');
  const [stateFilter, setStateFilter]   = useState("");
  const [allStates, setAllStates]       = useState([]);
  const [exportExtra, setExportExtra] = useState({
    bankName: '',
    bankType: '',
    category: '',
    state: '',
  });

  const companyList = useSelector((state) => state.companyList);
  const { companies = [] } = companyList;

  const uniqueStates      = [...new Set(records.map((r) => r.state).filter(Boolean))].sort();
  const uniqueCategories  = [...new Set(records.map((r) => r.category).filter(Boolean))].sort();

  // Load company list and states on mount
  useEffect(() => {
    dispatch(listActiveCompanies());
    fetch(`${API_URL}/price-master/states`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setAllStates(d.data); })
      .catch(() => {});
  }, [dispatch]);

  const handleGenerate = async () => {
    setLoading(true);
    setGenerated(false);
    setRecords([]);
    setPage(0);
    try {
      const params = new URLSearchParams({ month, year });
      if (company) params.set("company", company);
      if (stateFilter) params.set("state", stateFilter);
      const res = await fetch(`${API_URL}/price-master/report?${params}`);
      if (!res.ok) {
        console.error("Failed to generate report:", res.status);
        return;
      }
      const data = await res.json();
      if (data.success) {
        // Flatten per-bank bankWise into top-level fields for easy table access
        const flat = data.data.map((r) => {
          const row = { ...r };
          for (const { caseField } of BANK_PAIRS) {
            row[`${caseField}_cases`] = r.bankWise?.[caseField]?.cases ?? 0;
            row[`${caseField}_total`] = r.bankWise?.[caseField]?.total ?? 0;
          }
          return row;
        });
        setRecords(flat);
        setGenerated(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleInlineSave = async (row, field, value) => {
    setInlineEdit({ empId: null, field: null });
    const newVal = Number(value) || 0;
    const payload = { empId: row.employeeId, company, month, year, [field]: newVal };
    try {
      const res = await fetch(`${API_URL}/price-master/report/adjust`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update');

      setRecords(prev => prev.map(r => {
        if (r.employeeId !== row.employeeId) return r;
        const updated = { ...r, [field]: newVal };
        const computedTotal = Math.round(((updated.totalAmount ?? 0) + (updated.totalReimbursement ?? 0) + (updated.conveyance ?? 0) - (updated.penalty ?? 0)) * 100) / 100;
        const tdsAmount  = Math.round(computedTotal * (updated.tdsPercent ?? 0) / 100 * 100) / 100;
        const newPayable = Math.round((computedTotal - tdsAmount - (updated.advance ?? 0)) * 100) / 100;
        return { ...updated, tdsAmount, payableAmount: newPayable };
      }));
      toast.success('Updated');
    } catch (error) {
      toast.error(error.message || 'Update failed');
    }
  };

  const handleBankExport = async (format) => {
    if (!company) {
      toast.error('Please select a company before exporting');
      return;
    }

    // Excel is generated directly on frontend using already-loaded records
    if (format === 'xlsx') {
      const companyDetails = companies.find((c) => c._id === company);
      if (!companyDetails) { toast.error('Company details not found'); return; }

      let rows = [...records];
      if (exportExtra.bankName) rows = rows.filter((r) => (r.bankName || '').toLowerCase().includes(exportExtra.bankName.toLowerCase()));
      if (exportExtra.bankType) rows = rows.filter((r) => r.bankType === exportExtra.bankType);
      if (exportExtra.category) rows = rows.filter((r) => r.category === exportExtra.category);
      if (exportExtra.state)    rows = rows.filter((r) => (r.state || '').toLowerCase() === exportExtra.state.toLowerCase());
      rows = rows.filter((r) => (r.remarksForAccount || '').toLowerCase() !== 'hold');
      rows = rows.filter((r) => (r.payableAmount || 0) > 0);

      if (rows.length === 0) { toast.error('No records found for the selected filters'); return; }

      const autoDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).replace(/ /g, '-');
      const sheetRows = rows.map((record) => {
        const msgType = record.bankType === 'Bob' ? 'IFT' : 'NEFT';
        return {
          'CUSTOM_DETAILS1': 'MSER',
          'Value Date': autoDate,
          'Message Type': msgType,
          'Debit Account No.': companyDetails.accountNumber,
          'Beneficiary Name': record.feName,
          'Payment Amount': Math.ceil((record.payableAmount || 0) - 0.5),
          'Beneficiary Bank Swift Code / IFSC Code': record.ifscCode,
          'Beneficiary Account No.': record.accountNo,
          'Transaction Type Code': msgType,
          'CUSTOM_DETAILS2': '',
          'CUSTOM_DETAILS3': '',
          'CUSTOM_DETAILS4': '',
          'CUSTOM_DETAILS5': '',
          'CUSTOM_DETAILS6': '',
          'Remarks': '',
          'Purpose Of Payment': month,
        };
      });

      const ws = XLSX.utils.json_to_sheet(sheetRows);
      ws['!cols'] = Object.keys(sheetRows[0]).map((h) => ({ wch: Math.max(h.length + 2, 14) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bank Sheet');
      XLSX.writeFile(wb, `verification-bank-sheet-${month}-${year}.xlsx`);
      toast.success('Excel file downloaded successfully');
      setExportDialog(false);
      return;
    }

    // TXT / DOC go via backend; date is auto-generated
    setExportLoading(true);
    try {
      const autoDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).replace(/ /g, '-');
      const payload = { month, year, company, ...exportExtra, date: autoDate, rows: records };
      if (format === 'txt') {
        await Promise.resolve(dispatch(exportVerificationBankSheetTXT(payload)));
        toast.success('TXT file downloaded successfully');
      } else {
        await Promise.resolve(dispatch(exportVerificationBankSheetDOC(payload)));
        toast.success('DOC file downloaded successfully');
      }
      setExportDialog(false);
    } catch (error) {
      toast.error(error.message || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExport = () => {
    const exportData = records.map((r, idx) => ({
      "S No":                   idx + 1,
      "Emp ID":                 r.employeeId,
      "FE Name":                r.feName,
      "PAN":                    r.pan,
      "Bank Name":              r.bankName,
      "Bank Type":              r.bankType,
      "Account No.":            r.accountNo,
      "IFSC Code":              r.ifscCode,
      "Remarks for Account":    r.remarksForAccount,
      "Main Portfolio":         r.mainPortfolio,
      "Zone":                   r.zone,
      "State":                  r.state,
      "Department":             r.department,
      "Retainership":           r.retainership,
      "No of Days on Field":    r.daysOnField,
      "Reimbursement / Day":    r.reimbursementPerDay,
      "Total Reimbursement":    r.totalReimbursement,
      "Total Cases":            r.totalCases,
      "Total Amount":           (r.totalAmount ?? 0) + (r.totalReimbursement ?? 0) + (r.conveyance ?? 0) - (r.penalty ?? 0),
      "TDS%":                   r.tdsPercent,
      "TDS Amount":             Math.round(((r.totalAmount ?? 0) + (r.totalReimbursement ?? 0) + (r.conveyance ?? 0) - (r.penalty ?? 0)) * (r.tdsPercent ?? 0) / 100 * 100) / 100,
      "Advance":                r.advance,
      "Conveyance":             r.conveyance,
      "Penalty":                r.penalty,
      "Payable Amount":         r.payableAmount,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    ws["!cols"] = Object.keys(exportData[0] || {}).map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Verification Report");
    XLSX.writeFile(wb, `verification_report_${month}_${year}.xlsx`);
  };

  const filteredRecords = records.filter((r) => {
    if (stateFilter && r.state !== stateFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (r.employeeId    || "").toLowerCase().includes(q) ||
      (r.feName        || "").toLowerCase().includes(q) ||
      (r.pan           || "").toLowerCase().includes(q) ||
      (r.bankName      || "").toLowerCase().includes(q) ||
      (r.zone          || "").toLowerCase().includes(q) ||
      (r.state         || "").toLowerCase().includes(q) ||
      (r.department    || "").toLowerCase().includes(q)
    );
  });

  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Verification Report</h2>
            <p className="text-sm text-gray-500 mt-1">
              Generate payroll report by month, year and company
            </p>
          </div>
          {generated && role.verificationReportExport !== false && (
            <div className="flex gap-2">
              <button
                onClick={handleExport}
                disabled={records.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={18} />
                Export to Excel
              </button>
              <button
                onClick={() => setExportDialog(true)}
                disabled={records.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={18} />
                Export Bank Sheet
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-end mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {MONTHS.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Company</InputLabel>
            <Select value={company} label="Company" onChange={(e) => setCompany(e.target.value)}>
              <MenuItem value="">All Companies</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c._id} value={c._id}>{c.companyName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>State</InputLabel>
            <Select
              value={stateFilter}
              label="State"
              onChange={(e) => { setStateFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All States</MenuItem>
              {[...new Set([...allStates, ...uniqueStates])].sort().map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {role.verificationReportGenerate !== false && (
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  Generating...
                </>
              ) : (
                "Generate Report"
              )}
            </button>
          )}
        </div>

        {/* Pre-generate prompt */}
        {!generated && !loading && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-lg">
              Select month, year and company, then click{" "}
              <span className="font-semibold text-green-600">Generate Report</span>
            </p>
          </div>
        )}

        {/* Results */}
        {generated && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                <p className="text-xs text-indigo-600 font-medium uppercase tracking-wide">
                  Total Employees
                </p>
                <p className="text-2xl font-bold text-indigo-700 mt-1">
                  {records.length}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <p className="text-xs text-green-600 font-medium uppercase tracking-wide">
                  Total Reimbursement
                </p>
                <p className="text-2xl font-bold text-green-700 mt-1">
                  ₹{records.reduce((s, r) => s + (r.totalReimbursement ?? 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">
                  Total Cases
                </p>
                <p className="text-2xl font-bold text-purple-700 mt-1">
                  {records.reduce((s, r) => s + (r.totalCases ?? 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 border border-teal-100">
                <p className="text-xs text-teal-600 font-medium uppercase tracking-wide">
                  Total Payable
                </p>
                <p className="text-2xl font-bold text-teal-700 mt-1">
                  ₹{records.filter(r => (r.remarksForAccount || "").toLowerCase() !== "hold").reduce((s, r) => s + (r.payableAmount ?? 0), 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Search */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Table */}
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                      {COLUMNS.map((col) => (
                        <TableCell
                          key={col.field}
                          style={{ fontSize: 12, fontWeight: "bold", whiteSpace: "nowrap" }}
                        >
                          {col.title}
                          {col.editable && role.verificationReportEdit !== false && (
                            <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: 4 }}>✎</span>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRecords.length > 0 ? (
                      paginatedRecords.map((row, idx) => (
                        <TableRow key={row.employeeId || idx} hover>
                          {COLUMNS.map((col) => {
                            if (col.field === "sNo") {
                              return (
                                <TableCell key={col.field} style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                  {page * rowsPerPage + idx + 1}
                                </TableCell>
                              );
                            }
                            if (col.editable && role.verificationReportEdit !== false) {
                              const isEditing =
                                inlineEdit.empId === row.employeeId &&
                                inlineEdit.field === col.field;
                              return (
                                <TableCell key={col.field} style={{ fontSize: 12, whiteSpace: "nowrap", padding: "4px 8px" }}>
                                  {isEditing ? (
                                    <input
                                      autoFocus
                                      type="number"
                                      value={inlineValue}
                                      onChange={(e) => setInlineValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleInlineSave(row, col.field, inlineValue);
                                        if (e.key === "Escape") setInlineEdit({ empId: null, field: null });
                                      }}
                                      onBlur={() => handleInlineSave(row, col.field, inlineValue)}
                                      style={{
                                        width: 80, fontSize: 12, padding: "2px 4px",
                                        border: "1px solid #6366f1", borderRadius: 4, outline: "none",
                                      }}
                                    />
                                  ) : (
                                    <span
                                      onClick={() => {
                                        setInlineEdit({ empId: row.employeeId, field: col.field });
                                        setInlineValue(row[col.field] ?? 0);
                                      }}
                                      title="Click to edit"
                                      style={{
                                        fontWeight: 500, cursor: "pointer",
                                        borderBottom: "1px dashed #a5b4fc",
                                        color: col.color || "inherit",
                                      }}
                                    >
                                      ₹{Number(row[col.field] ?? 0).toLocaleString()}
                                    </span>
                                  )}
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell key={col.field} style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                {renderCell(row, col)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredRecords.length > 0 && (() => {
                      const SUMMED_FIELDS = new Set([
                        "retainership", "daysOnField", "reimbursementPerDay",
                        "totalCases", "conveyance", "penalty",
                        "totalReimbursement", "advance", "payableAmount",
                      ]);
                      return (
                        <TableRow sx={{ backgroundColor: "#f1f5f9", fontWeight: "bold" }}>
                          {COLUMNS.map((col, i) => {
                            if (i === 0) {
                              return (
                                <TableCell key={col.field} style={{ fontSize: 12, fontWeight: "bold", whiteSpace: "nowrap" }}>
                                  Total
                                </TableCell>
                              );
                            }
                            if (col.field === "totalAmount" || col.field === "tdsAmount") {
                              const sum = filteredRecords.filter(r => (r.remarksForAccount || "").toLowerCase() !== "hold").reduce((s, r) => s + (col.getValue(r)), 0);
                              return (
                                <TableCell key={col.field} style={{ fontSize: 12, fontWeight: "bold", whiteSpace: "nowrap", color: col.color }}>
                                  ₹{Number(sum).toLocaleString()}
                                </TableCell>
                              );
                            }
                            if (SUMMED_FIELDS.has(col.field)) {
                              const sum = filteredRecords.filter(r => (r.remarksForAccount || "").toLowerCase() !== "hold").reduce((s, r) => s + (r[col.field] ?? 0), 0);
                              return (
                                <TableCell key={col.field} style={{ fontSize: 12, fontWeight: "bold", whiteSpace: "nowrap", color: col.color }}>
                                  {col.currency ? `₹${Number(sum).toLocaleString()}` : Number(sum).toLocaleString()}
                                </TableCell>
                              );
                            }
                            return <TableCell key={col.field} />;
                          })}
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[15, 30, 50, 100]}
                component="div"
                count={filteredRecords.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Paper>
          </>
        )}
      </div>

      {/* Export Bank Sheet Dialog */}
      <Dialog open={exportDialog} onClose={() => setExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Bank Sheet</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Month" value={month} disabled />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Year" value={year} disabled />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Name (Optional Filter)"
                value={exportExtra.bankName}
                onChange={(e) => setExportExtra({ ...exportExtra, bankName: e.target.value })}
                helperText="Leave empty to export all banks"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category (Optional Filter)</InputLabel>
                <Select
                  value={exportExtra.category}
                  onChange={(e) => setExportExtra({ ...exportExtra, category: e.target.value })}
                  label="Category (Optional Filter)"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {uniqueCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Bank Type (Optional Filter)</InputLabel>
                <Select
                  value={exportExtra.bankType}
                  onChange={(e) => setExportExtra({ ...exportExtra, bankType: e.target.value })}
                  label="Bank Type (Optional Filter)"
                >
                  <MenuItem value="">All Bank Types</MenuItem>
                  <MenuItem value="Bob">Bob</MenuItem>
                  <MenuItem value="Non-Bob">Non-Bob</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>State (Optional Filter)</InputLabel>
                <Select
                  value={exportExtra.state}
                  onChange={(e) => setExportExtra({ ...exportExtra, state: e.target.value })}
                  label="State (Optional Filter)"
                >
                  <MenuItem value="">All States</MenuItem>
                  {uniqueStates.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleBankExport('txt')}
            disabled={exportLoading}
            startIcon={<DownloadIcon />}
          >
            Export TXT
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleBankExport('doc')}
            disabled={exportLoading}
            startIcon={<DownloadIcon />}
          >
            Export DOC
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => handleBankExport('xlsx')}
            disabled={exportLoading}
            startIcon={<DownloadIcon />}
          >
            Export Excel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VerificationReport;
